import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Edit3, Trash2, Check, Search, AlertTriangle, Calendar, Tag,
  TrendingDown, Wallet, Hourglass, AlertCircle, ArrowLeft,
  Copy as CopyIcon, FileText, Download, Filter, X as XIcon, BarChart3,
  Repeat, Banknote, Layers,
} from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "@/components/CurrencyInput";
import { DespesasExportDialog } from "@/components/ReportExportDialogs";

// ─────────────────────────────────────────────────────────────────────────────
// Despesas — Controle profissional (fixas recorrentes + únicas)
// ─────────────────────────────────────────────────────────────────────────────

const fmtBR = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T12:00:00").toLocaleDateString("pt-BR"); } catch { return iso; }
};
const todayStr = () => new Date().toISOString().slice(0, 10);
const ymStr = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const PAYMENT_METHODS = ["PIX", "Dinheiro", "Cartão", "Boleto", "Transferência", "Outro"];
const STATUS_LABEL = {
  paid: { txt: "Pago", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
  pending: { txt: "Pendente", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
  overdue: { txt: "Atrasada", cls: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" },
};

function StatusBadge({ status }) {
  const m = STATUS_LABEL[status] || STATUS_LABEL.pending;
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${m.cls}`}>{m.txt}</span>;
}

// ── Categorias ───────────────────────────────────────────────────────────────
function CategoriesSection({ categories, onChange }) {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#10B981");
  const [editing, setEditing] = useState(null);
  const create = async () => {
    if (!name.trim()) return toast.error("Informe um nome");
    try {
      if (editing) {
        await api.put(`/expenses/categories/${editing.id}`, { name: name.trim(), color });
        toast.success("Categoria atualizada");
      } else {
        await api.post("/expenses/categories", { name: name.trim(), color });
        toast.success("Categoria criada");
      }
      setName(""); setColor("#10B981"); setEditing(null);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };
  const remove = async (cat) => {
    if (!window.confirm(`Excluir categoria "${cat.name}"?`)) return;
    try {
      await api.delete(`/expenses/categories/${cat.id}`);
      toast.success("Excluída");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };
  const edit = (c) => { setEditing(c); setName(c.name); setColor(c.color || "#10B981"); };
  return (
    <Card className="p-5 border-[#E5E5E5] dark:border-white/10 shadow-none space-y-4" data-testid="categories-section">
      <h2 className="text-base font-display flex items-center gap-2"><Tag size={16}/> Categorias de despesas</h2>
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Nome</Label>
          <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Ex: Aluguel, Luz, Salários…" data-testid="cat-name-input"/>
        </div>
        <div className="w-[80px]">
          <Label className="text-xs">Cor</Label>
          <input type="color" value={color} onChange={e=>setColor(e.target.value)} className="w-full h-10 rounded border border-[#E5E5E5] cursor-pointer" data-testid="cat-color-input"/>
        </div>
        <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10" data-testid="cat-save-btn">
          {editing ? <><Check size={14} className="mr-1"/> Salvar</> : <><Plus size={14} className="mr-1"/> Criar</>}
        </Button>
        {editing && <Button variant="outline" onClick={()=>{setEditing(null);setName("");setColor("#10B981");}}>Cancelar</Button>}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {categories.length === 0 && (
          <div className="col-span-full text-xs text-neutral-400 italic text-center py-4">Nenhuma categoria criada.</div>
        )}
        {categories.map(c => (
          <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 border border-[#E5E5E5] dark:border-white/10 rounded-md" data-testid={`cat-item-${c.id}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{backgroundColor: c.color || "#10B981"}}/>
              <span className="text-sm truncate">{c.name}</span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={()=>edit(c)} className="p-1 hover:text-emerald-600" data-testid={`cat-edit-${c.id}`}><Edit3 size={12}/></button>
              <button onClick={()=>remove(c)} className="p-1 hover:text-red-600" data-testid={`cat-del-${c.id}`}><Trash2 size={12}/></button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Modal cadastrar/editar despesa ───────────────────────────────────────────
function ExpenseDialog({ open, onClose, expense, categories, onSaved }) {
  const isEdit = !!expense;
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [type, setType] = useState("fixed");
  const [amount, setAmount] = useState(0);
  const [dueDate, setDueDate] = useState(todayStr());
  const [hasEnd, setHasEnd] = useState(false);
  const [endDate, setEndDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("PIX");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setName(expense.name || "");
      setCategoryId(expense.category_id || "");
      setType(expense.type || "single");
      setAmount(Number(expense.amount || 0));
      setDueDate(expense.due_date || todayStr());
      // Para fixas, vem do enrich do backend (has_end + end_date da série)
      setHasEnd(!!expense.has_end);
      setEndDate(expense.end_date || "");
      setPaymentMethod(expense.payment_method || "PIX");
      setNotes(expense.notes || "");
    } else {
      setName(""); setCategoryId(""); setType("fixed");
      setAmount(0); setDueDate(todayStr());
      setHasEnd(false); setEndDate("");
      setPaymentMethod("PIX"); setNotes("");
    }
  }, [open, expense, isEdit]);

  const submit = async () => {
    if (!name.trim()) return toast.error("Informe o nome");
    if (!amount || amount <= 0) return toast.error("Informe um valor maior que zero");
    if (!dueDate) return toast.error("Informe a data de vencimento");
    if (type === "fixed" && hasEnd && !endDate) return toast.error("Informe a data de término");
    const payload = {
      name: name.trim(),
      category_id: categoryId || null,
      type,
      amount: Number(amount),
      due_date: dueDate,
      has_end: type === "fixed" && hasEnd,
      end_date: type === "fixed" && hasEnd ? endDate : null,
      payment_method: paymentMethod,
      notes: notes.trim() || null,
    };
    try {
      if (isEdit) {
        await api.put(`/expenses/${expense.id}`, payload);
        toast.success("Despesa atualizada");
      } else {
        await api.post("/expenses", payload);
        toast.success(type === "fixed" ? "Despesa fixa criada (cobranças mensais geradas)" : "Despesa criada");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar despesa" : "Nova despesa"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nome da despesa</Label>
            <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Ex: Aluguel da loja" data-testid="exp-name"/>
          </div>
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={categoryId || "__none__"} onValueChange={(v)=>setCategoryId(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="exp-category"><SelectValue placeholder="Selecione…"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Sem categoria</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs">Tipo</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={()=>setType("fixed")} className={`px-3 py-2 text-xs rounded-md border ${type==="fixed" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="exp-type-fixed"><Repeat size={12} className="inline mr-1"/> Fixa (mensal)</button>
                <button type="button" onClick={()=>setType("single")} className={`px-3 py-2 text-xs rounded-md border ${type==="single" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5]"}`} data-testid="exp-type-single"><Banknote size={12} className="inline mr-1"/> Avulsa</button>
              </div>
              <p className="text-[10px] text-neutral-500 mt-1">{type === "fixed" ? "Repete automaticamente todos os meses." : "Aparece somente neste mês."}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Valor</Label>
              <CurrencyInput value={amount} onChange={setAmount} data-testid="exp-amount"/>
            </div>
            <div>
              <Label className="text-xs">Vencimento</Label>
              <Input type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)} data-testid="exp-due-date"/>
            </div>
          </div>
          {type === "fixed" && (
            <div className="border border-[#E5E5E5] dark:border-white/10 rounded-md p-2 space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={hasEnd} onChange={e=>setHasEnd(e.target.checked)} data-testid="exp-has-end"/>
                Tem término (parcelamento por tempo definido)
              </label>
              {hasEnd && (
                <div>
                  <Label className="text-xs">Última cobrança</Label>
                  <Input type="date" value={endDate || ""} onChange={e=>setEndDate(e.target.value)} data-testid="exp-end-date"/>
                  <p className="text-[10px] text-neutral-500 mt-1">A cobrança automática para nesta data. {isEdit && "Ao reduzir, parcelas futuras não pagas posteriores são removidas."}</p>
                </div>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Forma de pagamento</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger data-testid="exp-payment-method"><SelectValue/></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Observações</Label>
            <Textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Opcional" rows={2}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="exp-save-btn">
            <Check size={14} className="mr-1"/> {isEdit ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Modal de pagamento ───────────────────────────────────────────────────────
function PayDialog({ open, onClose, expense, onPaid }) {
  const [amount, setAmount] = useState(0);
  const [paidAt, setPaidAt] = useState(todayStr());
  const [paymentMethod, setPaymentMethod] = useState("PIX");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!open || !expense) return;
    setAmount(Number(expense.amount || 0));
    setPaidAt(todayStr());
    setPaymentMethod(expense.payment_method || "PIX");
    setNote("");
  }, [open, expense]);
  if (!open || !expense) return null;
  const submit = async () => {
    try {
      await api.post(`/expenses/${expense.id}/pay`, {
        amount: Number(amount), paid_at: paidAt,
        payment_method: paymentMethod, note: note.trim() || null,
      });
      toast.success("Despesa marcada como paga");
      onPaid(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pay-expense-dialog">
        <DialogHeader><DialogTitle>Marcar como paga</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="text-xs text-neutral-500">{expense.name} · vence {fmtBR(expense.due_date)}</div>
          <div>
            <Label className="text-xs">Valor pago</Label>
            <CurrencyInput value={amount} onChange={setAmount} data-testid="pay-amount"/>
          </div>
          <div>
            <Label className="text-xs">Data do pagamento</Label>
            <Input type="date" value={paidAt} onChange={e=>setPaidAt(e.target.value)}/>
          </div>
          <div>
            <Label className="text-xs">Forma de pagamento</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Observação (opcional)</Label>
            <Input value={note} onChange={e=>setNote(e.target.value)}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pay-confirm">
            <Check size={14} className="mr-1"/> Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Modal histórico ──────────────────────────────────────────────────────────
function HistoryDialog({ open, onClose, expense }) {
  if (!open || !expense) return null;
  const items = expense.history || [];
  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
        <DialogHeader><DialogTitle>Histórico — {expense.name}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {items.length === 0 && <div className="text-xs text-neutral-400 italic">Sem histórico.</div>}
          {items.map((h, i) => (
            <div key={i} className="text-xs border-l-2 border-emerald-500 pl-2 py-1">
              <div className="font-medium">{h.action}</div>
              <div className="text-neutral-500">{h.at?.slice(0, 16).replace("T", " ")}</div>
              <div>{h.detail}</div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Card de despesa (linha) ──────────────────────────────────────────────────
function ExpenseRow({ expense, onChange, categories }) {
  const [paying, setPaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteFixedOpen, setDeleteFixedOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState("this");

  const remove = () => {
    if (expense.type === "fixed" && expense.series_id) {
      setDeleteMode("this");
      setDeleteFixedOpen(true);
      return;
    }
    if (!window.confirm("Excluir esta despesa?")) return;
    (async () => {
      try {
        await api.delete(`/expenses/${expense.id}`);
        toast.success("Excluída");
        onChange();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erro");
      }
    })();
  };

  const confirmDeleteFixed = async () => {
    try {
      const r = await api.delete(`/expenses/${expense.id}?mode=${deleteMode}`);
      toast.success(r.data.deleted > 1
        ? `${r.data.deleted} ocorrências excluídas`
        : "Despesa excluída");
      setDeleteFixedOpen(false);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const duplicate = async () => {
    try {
      await api.post(`/expenses/${expense.id}/duplicate`);
      toast.success("Despesa duplicada");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const unpay = async () => {
    if (!window.confirm("Desfazer o pagamento desta despesa?")) return;
    try {
      await api.post(`/expenses/${expense.id}/unpay`);
      toast.success("Pagamento desfeito");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  const borderCls = {
    paid: "border-l-emerald-500",
    pending: "border-l-amber-500",
    overdue: "border-l-red-500",
  }[expense.status] || "border-l-neutral-300";

  return (
    <>
      <div className={`border border-[#E5E5E5] dark:border-white/10 border-l-4 ${borderCls} rounded-md bg-white dark:bg-[#0A0A0A] p-3`} data-testid={`expense-row-${expense.id}`}>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate max-w-[260px]">{expense.name}</span>
              <StatusBadge status={expense.status}/>
              {expense.type === "fixed" && (
                <span className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-0.5"><Repeat size={10}/> Fixa</span>
              )}
              {expense.category_name && (
                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{backgroundColor: (expense.category_color || "#10B981") + "22", color: expense.category_color || "#10B981"}}>{expense.category_name}</span>
              )}
            </div>
            <div className="text-[11px] text-neutral-500 mt-1 inline-flex items-center gap-2 flex-wrap">
              <span><Calendar size={10} className="inline mr-1"/> {fmtBR(expense.due_date)}</span>
              {expense.payment_method && <span>· {expense.payment_method}</span>}
              {expense.paid_at && <span>· pago em {fmtBR(expense.paid_at)}</span>}
            </div>
            {expense.notes && <div className="text-[11px] text-neutral-400 italic mt-1">{expense.notes}</div>}
          </div>
          <div className="text-right">
            <div className="font-display text-lg">{brl(expense.amount)}</div>
            {expense.paid_at && expense.paid_amount && Number(expense.paid_amount) !== Number(expense.amount) && (
              <div className="text-[10px] text-neutral-500">pago: {brl(expense.paid_amount)}</div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-[#F0F0F0] dark:border-white/5">
          {expense.status !== "paid" ? (
            <Button size="sm" onClick={()=>setPaying(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs" data-testid={`btn-pay-${expense.id}`}><Check size={11} className="mr-1"/> Marcar paga</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={unpay} className="h-7 text-xs" data-testid={`btn-unpay-${expense.id}`}>Desfazer pgto</Button>
          )}
          <Button size="sm" variant="outline" onClick={()=>setEditing(true)} className="h-7 text-xs" data-testid={`btn-edit-${expense.id}`}><Edit3 size={11} className="mr-1"/> Editar</Button>
          <Button size="sm" variant="outline" onClick={duplicate} className="h-7 text-xs" data-testid={`btn-dup-${expense.id}`}><CopyIcon size={11} className="mr-1"/> Duplicar</Button>
          <Button size="sm" variant="outline" onClick={()=>setHistoryOpen(true)} className="h-7 text-xs"><FileText size={11} className="mr-1"/> Histórico</Button>
          <Button size="sm" variant="outline" onClick={remove} className="h-7 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20" data-testid={`btn-del-${expense.id}`}><Trash2 size={11} className="mr-1"/> Excluir</Button>
        </div>
      </div>
      <PayDialog open={paying} onClose={()=>setPaying(false)} expense={expense} onPaid={onChange}/>
      <ExpenseDialog open={editing} onClose={()=>setEditing(false)} expense={expense} categories={categories || []} onSaved={onChange}/>
      <HistoryDialog open={historyOpen} onClose={()=>setHistoryOpen(false)} expense={expense}/>
      {/* Modal de exclusão para despesas FIXAS — substitui o window.prompt
          antigo (que era confuso e fazia o usuário deletar 'future' achando
          que era 'this'). Agora com radio buttons claros. */}
      <Dialog open={deleteFixedOpen} onOpenChange={(o)=>!o && setDeleteFixedOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="delete-fixed-dialog">
          <DialogHeader>
            <DialogTitle>Excluir despesa fixa</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-neutral-500 mb-3">
            <b>{expense.name}</b> · venc. {fmtBR(expense.due_date)} · {brl(expense.amount)}
          </p>
          <div className="space-y-2 text-sm">
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-[#E5E5E5] dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5">
              <input type="radio" name="delmode" value="this" checked={deleteMode==="this"} onChange={()=>setDeleteMode("this")} className="mt-0.5" data-testid="del-mode-this"/>
              <div>
                <div className="font-medium">Apenas esta ocorrência</div>
                <div className="text-[11px] text-neutral-500">As parcelas dos outros meses continuam normalmente.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-[#E5E5E5] dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5">
              <input type="radio" name="delmode" value="future" checked={deleteMode==="future"} onChange={()=>setDeleteMode("future")} className="mt-0.5" data-testid="del-mode-future"/>
              <div>
                <div className="font-medium">Esta + todas as futuras</div>
                <div className="text-[11px] text-neutral-500">Para a série a partir desta. Parcelas anteriores ficam intactas.</div>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-[#E5E5E5] dark:border-white/10 hover:bg-neutral-50 dark:hover:bg-white/5">
              <input type="radio" name="delmode" value="all" checked={deleteMode==="all"} onChange={()=>setDeleteMode("all")} className="mt-0.5" data-testid="del-mode-all"/>
              <div>
                <div className="font-medium">Toda a série (todas em aberto)</div>
                <div className="text-[11px] text-neutral-500">Apaga todas as ocorrências não pagas, em qualquer mês.</div>
              </div>
            </label>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={()=>setDeleteFixedOpen(false)}>Cancelar</Button>
            <Button onClick={confirmDeleteFixed} className="bg-red-600 hover:bg-red-700 text-white" data-testid="del-confirm-btn">
              <Trash2 size={14} className="mr-1"/> Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function DashboardSection({ summary, loading }) {
  if (loading || !summary) {
    return <div className="text-center text-neutral-400 text-sm py-8">Carregando…</div>;
  }
  const { cards, by_category, monthly, upcoming, overdue_list } = summary;
  const pctPaid = cards.total > 0 ? (cards.paid / cards.total) * 100 : 0;
  const maxMonthly = Math.max(1, ...monthly.map(m => m.total));
  const maxCategory = Math.max(1, ...(by_category || []).map(c => c.total));

  return (
    <div className="space-y-5">
      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-cards">
        <Card className="p-4 border-[#E5E5E5] dark:border-white/10 shadow-none">
          <div className="flex items-center justify-between text-xs text-neutral-500"><span>Total do mês</span><TrendingDown size={14}/></div>
          <div className="font-display text-2xl mt-2">{brl(cards.total)}</div>
          <div className="text-[10px] text-neutral-400 mt-1">{cards.count} despesa(s)</div>
        </Card>
        <Card className="p-4 border-[#E5E5E5] dark:border-white/10 shadow-none border-l-4 border-l-emerald-500">
          <div className="flex items-center justify-between text-xs text-emerald-600"><span>Pago</span><Wallet size={14}/></div>
          <div className="font-display text-2xl mt-2 text-emerald-700 dark:text-emerald-400">{brl(cards.paid)}</div>
          <div className="text-[10px] text-neutral-400 mt-1">{pctPaid.toFixed(0)}% do total</div>
        </Card>
        <Card className="p-4 border-[#E5E5E5] dark:border-white/10 shadow-none border-l-4 border-l-amber-500">
          <div className="flex items-center justify-between text-xs text-amber-700"><span>Pendente</span><Hourglass size={14}/></div>
          <div className="font-display text-2xl mt-2 text-amber-700 dark:text-amber-400">{brl(cards.pending)}</div>
        </Card>
        <Card className="p-4 border-[#E5E5E5] dark:border-white/10 shadow-none border-l-4 border-l-red-500">
          <div className="flex items-center justify-between text-xs text-red-600"><span>Atrasada</span><AlertCircle size={14}/></div>
          <div className="font-display text-2xl mt-2 text-red-700 dark:text-red-400">{brl(cards.overdue)}</div>
        </Card>
      </div>

      {/* Alertas */}
      {(overdue_list.length > 0 || upcoming.length > 0) && (
        <div className="grid lg:grid-cols-2 gap-3">
          {overdue_list.length > 0 && (
            <Card className="p-4 border-[#E5E5E5] dark:border-white/10 shadow-none border-l-4 border-l-red-500" data-testid="alert-overdue">
              <h3 className="text-sm font-display text-red-700 dark:text-red-400 flex items-center gap-1"><AlertTriangle size={14}/> Atrasadas ({overdue_list.length})</h3>
              <div className="mt-2 space-y-1">
                {overdue_list.slice(0, 5).map(o => (
                  <div key={o.id} className="flex justify-between text-xs">
                    <span className="truncate">{o.name}</span>
                    <span className="text-red-600 font-medium">{brl(o.amount)} · {fmtBR(o.due_date)}</span>
                  </div>
                ))}
                {overdue_list.length > 5 && <div className="text-[10px] text-neutral-400">+ {overdue_list.length - 5} outras…</div>}
              </div>
            </Card>
          )}
          {upcoming.length > 0 && (
            <Card className="p-4 border-[#E5E5E5] dark:border-white/10 shadow-none border-l-4 border-l-amber-500" data-testid="alert-upcoming">
              <h3 className="text-sm font-display text-amber-700 dark:text-amber-400 flex items-center gap-1"><Calendar size={14}/> A vencer (próx. 7 dias)</h3>
              <div className="mt-2 space-y-1">
                {upcoming.slice(0, 5).map(o => (
                  <div key={o.id} className="flex justify-between text-xs">
                    <span className="truncate">{o.name}</span>
                    <span className="text-amber-700 font-medium">{brl(o.amount)} · {fmtBR(o.due_date)}</span>
                  </div>
                ))}
                {upcoming.length > 5 && <div className="text-[10px] text-neutral-400">+ {upcoming.length - 5} outras…</div>}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Gráficos */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 border-[#E5E5E5] dark:border-white/10 shadow-none" data-testid="chart-categories">
          <h3 className="text-sm font-display flex items-center gap-1 mb-3"><Layers size={14}/> Gastos por categoria</h3>
          {by_category.length === 0 ? (
            <div className="text-xs text-neutral-400 italic text-center py-4">Sem dados.</div>
          ) : (
            <div className="space-y-2">
              {by_category.map(c => (
                <div key={c.category_id || "none"}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="truncate">{c.category_name}</span>
                    <span className="font-medium">{brl(c.total)} · {c.count}</span>
                  </div>
                  <div className="w-full h-2 bg-neutral-100 dark:bg-white/5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{
                      width: `${(c.total / maxCategory) * 100}%`,
                      backgroundColor: c.category_color || "#10B981"}}/>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5 border-[#E5E5E5] dark:border-white/10 shadow-none" data-testid="chart-monthly">
          <h3 className="text-sm font-display flex items-center gap-1 mb-3"><BarChart3 size={14}/> Comparativo mensal</h3>
          <div className="flex items-end justify-between gap-2 h-40 px-1">
            {monthly.map(m => {
              const h = (m.total / maxMonthly) * 100;
              const hp = m.total > 0 ? (m.paid / m.total) * h : 0;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[9px] text-neutral-500">{brl(m.total)}</div>
                  <div className="w-full bg-neutral-100 dark:bg-white/5 rounded-t-md relative" style={{height: "120px"}}>
                    <div className="absolute bottom-0 left-0 right-0 bg-amber-300 rounded-t-md" style={{height: `${h}%`}}/>
                    <div className="absolute bottom-0 left-0 right-0 bg-emerald-500 rounded-t-md" style={{height: `${hp}%`}}/>
                  </div>
                  <div className="text-[10px] text-neutral-500">{m.month_label}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 text-[10px] text-neutral-500 mt-2">
            <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1"/> Pago</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-amber-300 mr-1"/> Pendente/Atrasado</span>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── Filtros + Lista ───────────────────────────────────────────────────────────
function ExpensesList({ expenses, categories, onChange, onAdd }) {
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");

  const filtered = useMemo(() => {
    return expenses.filter(e => {
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      if (typeFilter !== "all" && e.type !== typeFilter) return false;
      if (catFilter !== "all" && (e.category_id || "") !== catFilter) return false;
      if (q.trim()) {
        const blob = ((e.name || "") + " " + (e.notes || "")).toLowerCase();
        if (!blob.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [expenses, q, statusFilter, typeFilter, catFilter]);

  const total = filtered.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Buscar despesa…" className="pl-9" data-testid="search-input"/>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] h-9 text-xs" data-testid="filter-status"><Filter size={12} className="mr-1"/> <SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="paid">Pagas</SelectItem>
            <SelectItem value="overdue">Atrasadas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[130px] h-9 text-xs" data-testid="filter-type"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos tipos</SelectItem>
            <SelectItem value="fixed">Fixas</SelectItem>
            <SelectItem value="single">Avulsas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-[170px] h-9 text-xs" data-testid="filter-cat"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={onAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9" data-testid="add-expense-btn">
          <Plus size={14} className="mr-1"/> Nova despesa
        </Button>
      </div>

      <div className="text-xs text-neutral-500">
        {filtered.length} resultado(s) · Total: <span className="font-medium text-neutral-900 dark:text-neutral-100">{brl(total)}</span>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && <div className="text-center text-xs text-neutral-400 italic py-8">Nenhuma despesa encontrada.</div>}
        {filtered.map(e => <ExpenseRow key={e.id} expense={e} onChange={onChange} categories={categories}/>)}
      </div>
    </div>
  );
}

// ── Relatórios (export) ──────────────────────────────────────────────────────
function ReportsSection({ expenses, summary, monthLabel }) {
  const exportCSV = () => {
    const rows = [
      ["Nome", "Categoria", "Tipo", "Valor", "Vencimento", "Status", "Pago em", "Forma de pagto", "Observações"],
      ...expenses.map(e => [
        e.name, e.category_name || "—", e.type === "fixed" ? "Fixa" : "Avulsa",
        Number(e.amount || 0).toFixed(2).replace(".", ","), fmtBR(e.due_date),
        e.status === "paid" ? "Pago" : e.status === "overdue" ? "Atrasada" : "Pendente",
        e.paid_at ? fmtBR(e.paid_at) : "",
        e.payment_method || "",
        (e.notes || "").replace(/"/g, "''")
      ])
    ];
    const csv = rows.map(r => r.map(c => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `despesas-${monthLabel}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPDF = () => {
    const totals = summary?.cards || { total: 0, paid: 0, pending: 0, overdue: 0, count: 0 };
    const html = `
<!doctype html><html><head><meta charset="utf-8"><title>Despesas ${monthLabel}</title>
<style>
* { box-sizing: border-box; }
body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 24px; color: #111; }
h1 { font-size: 22px; margin: 0 0 4px; }
.sub { color: #666; font-size: 12px; margin-bottom: 16px; }
.cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
.card { border: 1px solid #ddd; border-radius: 6px; padding: 10px; }
.card h3 { font-size: 10px; text-transform: uppercase; color: #888; margin: 0 0 4px; }
.card .v { font-size: 18px; font-weight: bold; }
table { width: 100%; border-collapse: collapse; font-size: 11px; }
th, td { padding: 6px; text-align: left; border-bottom: 1px solid #eee; }
th { background: #f5f5f5; text-transform: uppercase; font-size: 10px; }
.paid { color: #047857; }
.overdue { color: #b91c1c; }
.pending { color: #b45309; }
.right { text-align: right; }
@media print { body { padding: 12px; } }
</style></head><body>
<h1>Relatório de despesas</h1>
<div class="sub">Período: ${monthLabel} · Gerado em ${new Date().toLocaleString("pt-BR")}</div>
<div class="cards">
  <div class="card"><h3>Total</h3><div class="v">${brl(totals.total)}</div></div>
  <div class="card"><h3>Pago</h3><div class="v paid">${brl(totals.paid)}</div></div>
  <div class="card"><h3>Pendente</h3><div class="v pending">${brl(totals.pending)}</div></div>
  <div class="card"><h3>Atrasada</h3><div class="v overdue">${brl(totals.overdue)}</div></div>
</div>
<table>
  <thead><tr>
    <th>Despesa</th><th>Categoria</th><th>Tipo</th><th>Vencimento</th><th>Status</th><th class="right">Valor</th>
  </tr></thead>
  <tbody>
${expenses.map(e => `
    <tr>
      <td>${e.name || ""}</td>
      <td>${e.category_name || "—"}</td>
      <td>${e.type === "fixed" ? "Fixa" : "Avulsa"}</td>
      <td>${fmtBR(e.due_date)}</td>
      <td class="${e.status}">${e.status === "paid" ? "Pago" : e.status === "overdue" ? "Atrasada" : "Pendente"}${e.paid_at ? " · " + fmtBR(e.paid_at) : ""}</td>
      <td class="right">${brl(e.amount)}</td>
    </tr>`).join("")}
  </tbody>
</table>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permita popups para exportar"); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 350);
  };

  return (
    <Card className="p-5 border-[#E5E5E5] dark:border-white/10 shadow-none space-y-3" data-testid="reports-section">
      <h2 className="text-base font-display flex items-center gap-2"><FileText size={16}/> Relatórios</h2>
      <p className="text-xs text-neutral-500">Exporte os dados do período filtrado para CSV (Excel) ou PDF.</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={exportCSV} variant="outline" data-testid="export-csv-btn"><Download size={14} className="mr-1"/> Exportar CSV (Excel)</Button>
        <Button onClick={exportPDF} className="bg-[#0A0A0A] text-white hover:bg-black" data-testid="export-pdf-btn"><FileText size={14} className="mr-1"/> Exportar PDF</Button>
      </div>
    </Card>
  );
}

// ── Página principal ─────────────────────────────────────────────────────────
export default function Despesas() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [month, setMonth] = useState(ymStr());
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [eR, cR, sR] = await Promise.all([
        api.get("/expenses", { params: { month } }),
        api.get("/expenses/categories"),
        api.get("/expenses/dashboard/summary", { params: { month } }),
      ]);
      setExpenses(eR.data || []);
      setCategories(cR.data || []);
      setSummary(sR.data);
    } catch (e) {
      toast.error("Erro ao carregar despesas");
    } finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  // mês -1 / +1
  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const monthLabel = (() => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  })();

  return (
    <div className="space-y-6 p-2 sm:p-4" data-testid="despesas-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <button onClick={()=>navigate("/financeiro")} className="text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-white inline-flex items-center gap-1 mb-2" data-testid="back-btn">
            <ArrowLeft size={12}/> Voltar ao Financeiro
          </button>
          <p className="label-tiny mb-1">Financeiro</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Despesas</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={()=>shiftMonth(-1)} data-testid="prev-month-btn">‹</Button>
          <div className="text-sm font-medium capitalize min-w-[140px] text-center" data-testid="month-label">{monthLabel}</div>
          <Button variant="outline" size="sm" onClick={()=>shiftMonth(1)} data-testid="next-month-btn">›</Button>
          <Button variant="outline" onClick={()=>setExportOpen(true)} className="ml-2" data-testid="btn-export-despesas">
            <Download size={14} className="mr-1"/> Exportar relatório
          </Button>
          <Button onClick={()=>setAdding(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="header-add-expense-btn">
            <Plus size={14} className="mr-1"/> Nova despesa
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto" data-testid="tabs-root">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard"><BarChart3 size={12} className="mr-1"/> Dashboard</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list"><Layers size={12} className="mr-1"/> Todas despesas</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><Tag size={12} className="mr-1"/> Categorias</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><FileText size={12} className="mr-1"/> Relatórios</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardSection summary={summary} loading={loading}/>
        </TabsContent>

        <TabsContent value="list" className="mt-4">
          <ExpensesList expenses={expenses} categories={categories} onChange={load} onAdd={()=>setAdding(true)}/>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <CategoriesSection categories={categories} onChange={load}/>
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <ReportsSection expenses={expenses} summary={summary} monthLabel={monthLabel}/>
        </TabsContent>
      </Tabs>

      <ExpenseDialog
        open={adding}
        onClose={()=>setAdding(false)}
        expense={null}
        categories={categories}
        onSaved={()=>{ setTab("list"); load(); }}
      />
      <DespesasExportDialog open={exportOpen} onClose={()=>setExportOpen(false)}/>
    </div>
  );
}
